import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers";
import { useDictionary } from "hooks/use-dictionary";
import { useState } from "react";
import { PublicEventsFilters } from "types/event";
import { ProgramType } from "types/program";
import { MenuItem, Checkbox, FormControlLabel } from "@mui/material";
import dayjs from "dayjs";
import { usePublicOrganizerInstitutionsForEvents } from "hooks/api/use-public-organizer-institutions-for-events";
import { ClassifierType } from "types/classifier";
import FieldSelect from "components/field-select";
import { usePublicClassifierValues } from "hooks/api/system-admin/classifiers/use-public-classifier-values";
import { useUrlParams } from "hooks/use-url-params";

interface FiltersBarProps {
  onSubmit: (val: PublicEventsFilters) => void;
  onClear: () => void;
}

const FiltersBar = ({ onSubmit, onClear }: FiltersBarProps) => {
  const { filters: urlFilters, setUrlFilters } = useUrlParams();
  const defaultFilters: PublicEventsFilters = {
    name: null,
    startDate: null,
    endDate: null,
    organizerInstitution: null,
    developmentCompetencyGroupClassifier: null,
    programType: null,
    eventHours: null,
    review: null,
    price: null,
    otherTargetedAudienceClassifierId: null,
    targetedAudienceClassifierId: null,
    city: null,
    isRemote: null,
    includeExpired: null,
  };

  const [filters, setFilters] = useState<PublicEventsFilters>({
    ...defaultFilters,
    ...urlFilters,
  });
  const includeExpired = Boolean(filters.includeExpired);
  const { items: institutions } = usePublicOrganizerInstitutionsForEvents(includeExpired);
  const [isSelecting, setIsSelecting] = useState(false);
  const programType = [ProgramType.DEVELOPMENT_TYPE, ProgramType.LEARNING_TYPE];
  const dict = useDictionary();

  const { items: otherTargetedAudienceClassifiers, loadMore: otherTargetedAudienceClassifiersLoadMore, setFilters: setOtherTargetedAudienceClassifierFilters } = usePublicClassifierValues(ClassifierType.OTHER_TARGETED_AUDIENCE);
  const { items: targetedAudienceClassifiers, loadMore: targetedAudienceClassifiersLoadMore, setFilters: setTargetedAudienceClassifierFilters } = usePublicClassifierValues(ClassifierType.TARGETED_AUDIENCE);
  const { items: developmentCompetencyGroupClassifiers } = usePublicClassifierValues(ClassifierType.DEVELOPMENT_COMPETENCY_GROUP);
  const { items: cities, loadMore: citiesLoadMore, setFilters: setCityFilters } = usePublicClassifierValues(ClassifierType.CITY);

  return (
    <div className="rounded bg-white border p-5 grid sm:grid-cols-2 gap-5">
      <TextField
        label={dict.organizerAdmin.events.name}
        value={filters.name ?? ""}
        onChange={(e) => {
          setFilters((prev) => ({ ...prev, name: e.target.value || null }));
        }}
      />
      <DatePicker
        label={dict.organizerAdmin.events.eventStartDate}
        value={filters.startDate ? dayjs(filters.startDate) : null}
        format="YYYY-MM-DD"
        onChange={(val) => {
          setFilters((prev) => ({ ...prev, startDate: val ? val.format("YYYY-MM-DD") : null }));
        }}
      />
      <DatePicker
        label={dict.organizerAdmin.events.eventEndDate}
        value={filters.endDate ? dayjs(filters.endDate) : null}
        format="YYYY-MM-DD"
        onChange={(val) => {
          setFilters((prev) => ({ ...prev, endDate: val ? val.format("YYYY-MM-DD") : null }));
        }}
      />
      <TextField
        label={dict.organizerAdmin.events.organizer}
        value={filters.organizerInstitution ?? ""}
        select
        onChange={(e) => {
          setIsSelecting(true);
          setFilters((prev) => ({ ...prev, organizerInstitution: e.target.value || null }));
          setTimeout(() => setIsSelecting(false), 150);
        }}
      >
        {institutions.map((institution, i) => (
          <MenuItem key={i} value={institution.id} disabled={isSelecting}>
            {institution.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label={dict.organizerAdmin.events.developmentGroup}
        value={filters.developmentCompetencyGroupClassifier ?? ""}
        select
        onChange={(e) => {
          setIsSelecting(true);
          setFilters((prev) => ({
            ...prev,
            developmentCompetencyGroupClassifier: e.target.value || null,
          }));
          setTimeout(() => setIsSelecting(false), 150);
        }}
      >
        {developmentCompetencyGroupClassifiers.map((classifier) => (
          <MenuItem
            key={classifier.id}
            value={classifier.id}
            disabled={isSelecting}
          >
            {`${classifier.valueName} ${dict.organizerAdmin.events.competencySuffix}`}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label={dict.organizerAdmin.events.type}
        value={filters.programType ?? ""}
        select
        onChange={(e) => {
          setFilters((prev) => ({ ...prev, programType: (e.target.value as ProgramType) || null }));
        }}
      >
        {programType.map((type, i) => (
          <MenuItem key={i} value={type}>
            {dict.common.programType[type]}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label={dict.organizerAdmin.events.duration}
        type="number"
        value={filters.eventHours ?? ""}
        onWheel={(e) => (e.target as HTMLElement).blur()}
        onKeyDown={(e) => {
          if (["e", "-", "+", "."].includes(e.key.toLowerCase())) {
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          let val = parseFloat(e.target.value);
          if (val < 1) val = 1;
          setFilters((prev) => ({ ...prev, eventHours: val != null ? +val : null }));
        }}
        inputProps={{ min: 1 }}
      />
      <TextField
        label={dict.organizerAdmin.events.minimalReview}
        type="number"
        value={filters.review ?? ""}
        onWheel={(e) => (e.target as HTMLElement).blur()}
        onKeyDown={(e) => {
          if (["e", "-", "+"].includes(e.key.toLowerCase())) {
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          let val = parseFloat(e.target.value);
          if (val < 1) val = 1;
          if (val > 5) val = 5;
          setFilters((prev) => ({ ...prev, review: val }));
        }}
        inputProps={{ min: 1, max: 5, step: 0.1 }}
      />
      <TextField
        label={dict.organizerAdmin.events.maxPrice}
        type="number"
        value={filters.price ?? ""}
        onWheel={(e) => (e.target as HTMLElement).blur()}
        onKeyDown={(e) => {
          if (["e", "-", "+", "."].includes(e.key.toLowerCase())) {
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          let val = parseFloat(e.target.value);
          if (val < 1) val = 1;
          setFilters((prev) => ({ ...prev, price: val != null ? +val : null }));
        }}
        inputProps={{ min: 1 }}
      />
      <FieldSelect
        label={dict.organizerAdmin.events.targetedAudienceClassifier}
        options={targetedAudienceClassifiers.map((targetedAudienceClassifier) => ({ value: targetedAudienceClassifier.id, label: targetedAudienceClassifier.valueName }))}
        onChange={(val) => setFilters((prev) => ({ ...prev, targetedAudienceClassifierId: val }))}
        setFilters={setTargetedAudienceClassifierFilters} 
        lazyLoadable
        onBottomReached={targetedAudienceClassifiersLoadMore}
        value={filters.targetedAudienceClassifierId ?? ''}
      />
      <FieldSelect
        label={dict.organizerAdmin.events.city}
        options={cities.map((city) => ({ value: city.id, label: city.valueName }))}
        onChange={(val) => setFilters((prev) => ({ ...prev, city: val || null }))}
        setFilters={setCityFilters}
        lazyLoadable
        onBottomReached={citiesLoadMore}
        value={filters.city ?? ''}
      />
      <FieldSelect
        label={dict.organizerAdmin.events.otherTargetedAudienceClassifier}
        options={otherTargetedAudienceClassifiers.map((otherTargetedAudienceClassifier) => ({ value: otherTargetedAudienceClassifier.id, label: otherTargetedAudienceClassifier.valueName }))}
        onChange={(val) => setFilters((prev) => ({ ...prev, otherTargetedAudienceClassifierId: val }))}
        setFilters={setOtherTargetedAudienceClassifierFilters} 
        lazyLoadable
        onBottomReached={otherTargetedAudienceClassifiersLoadMore}
        value={filters.otherTargetedAudienceClassifierId ?? ''}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={filters.isRemote ?? false}
            onChange={(e) => {
              setFilters((prev) => ({
                ...prev,
                isRemote: e.target.checked ? true : null,
              }));
            }}
          />
        }
        label={dict.organizerAdmin.events.remoteTraining}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={includeExpired}
            onChange={(e) => {
              setFilters((prev) => ({
                ...prev,
                includeExpired: e.target.checked ? true : null,
              }));
            }}
          />
        }
        label={dict.common.includeExpiredEvents}
      />
      <div className="flex gap-3">
        <Button
          onClick={() => {
            onSubmit(filters);
            setUrlFilters(filters);
          }}
          color="primary"
          variant="contained"
          className="sm:w-[172px] flex-1 sm:flex-[unset]"
          type="submit"
        >
          {dict.common.search}
        </Button>
        <Button
          onClick={() => {
            onClear();
            setFilters(defaultFilters);
            setUrlFilters(defaultFilters);
          }}
          color="primary"
          variant="outlined"
          className="sm:w-[172px] flex-1 sm:flex-[unset]"
        >
          {dict.common.clear}
        </Button>
      </div>
    </div>
  );
};

export default FiltersBar;
